// src/app/seo/seo-content.service.ts
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Observable, map, shareReplay, switchMap, throwError } from 'rxjs';

export type CtaParams = { chainId: number; tokenIn: string; tokenOut: string };

// Bridge types
export type BridgeCtaParams = { fromChainId: number; toChainId: number; tokenIn: string; tokenOut: string };

export type BridgeSeoRoute = {
  locale: string;   // ex: 'fr' | 'en' | 'hi' ...
  country: string;  // ex: 'FR' | 'IN' ...
  slug: string;     // ex: 'pont-usdt-vers-eth'
  tKey: string;     // ex: 'BRIDGE_FR_USDT_ETH'
  ctaParams: BridgeCtaParams;
};

export type ResolvedBridgeSeo = {
  locale: string;
  slug: string;
  tKey: string;
  ctaParams: BridgeCtaParams;
  canonicalPath: string; // ex: /fr/pont-usdt-vers-eth
  page: {
    tKey: string;
    ctaParams: BridgeCtaParams;
    title?: string;
    metaDescription?: string;
    h1?: string;
    editorial?: string;
    faq?: { q: string; a: string }[];
  };
};

// Swap types
export type SeoRoute = {
  locale: string;   // 'hi' | 'vn' | 'id' | 'br' | 'tr' | 'fr' | 'en' ...
  country: string;  // 'IN' | 'VN' | 'ID' | 'BR' | 'TR' | ...
  slug: string;     // ex: 'usdt-se-usdc-badalna'
  tKey: string;     // ex: 'IN_USDT_USDC'
  ctaParams: CtaParams;
};

export type ResolvedSeo = {
  locale: string;
  slug: string;
  tKey: string;
  ctaParams: CtaParams;
  canonicalPath: string; // ex: /hi/usdt-se-usdc-badalna
  page: {
    tKey: string;
    ctaParams: CtaParams;
    title?: string;
    metaDescription?: string;
    h1?: string;
    editorial?: string;
    faq?: { q: string; a: string }[];
  };
};

// Alias compat
export type SeoResolved = ResolvedSeo;

@Injectable({ providedIn: 'root' })
export class SeoContentService {
  private http = inject(HttpClient);
  private i18n = inject(TranslateService);

  // ———————————————————————————————————
  // Locale helpers (ANTI-undefined.json)
  // ———————————————————————————————————
  private static readonly SUPPORTED_LOCALES = ['en', 'fr', 'hi', 'id', 'tr', 'vi', 'br','pt'];

  private normalizeLocale(loc?: string): string {
    return (loc || 'en').toLowerCase();
  }

  private safeLocale(loc?: string): string {
    const l = this.normalizeLocale(loc);
    return SeoContentService.SUPPORTED_LOCALES.includes(l) ? l : 'en';
  }

  // ———————————————————————————————————
  // SWAP routes
  // ———————————————————————————————————
  private routes$?: Observable<SeoRoute[]>;
  private loadRoutes(): Observable<SeoRoute[]> {
    if (!this.routes$) {
      this.routes$ = this.http
        .get<SeoRoute[]>('/assets/seo/seo_routes_20.json')
        .pipe(shareReplay(1));
    }
    return this.routes$!;
  }

  // ———————————————————————————————————
  // BRIDGE routes
  // ———————————————————————————————————
  private bridgeRoutes$?: Observable<BridgeSeoRoute[]>;
  private loadBridgeRoutes(): Observable<BridgeSeoRoute[]> {
    if (!this.bridgeRoutes$) {
      this.bridgeRoutes$ = this.http
        .get<BridgeSeoRoute[]>('/assets/seo/seo_routes_bridge.json')
        .pipe(shareReplay(1));
    }
    return this.bridgeRoutes$!;
  }

  // ———————————————————————————————————
  // BRIDGE resolver (inchangé, mais avec safeLocale)
  // ———————————————————————————————————
  getBridgeSeoBySlug(locale: string, slug: string): Observable<ResolvedBridgeSeo> {
    const loc = this.safeLocale(locale);
    const norm = (s: string) => (s || '').toLowerCase();

    return this.loadBridgeRoutes().pipe(
      map(list => {
        const slugNorm = norm(slug);
        let entry = Array.isArray(list)
          ? list.find(x => norm(x.locale) === loc && norm(x.slug) === slugNorm)
          : undefined;

        if (!entry) {
          entry = Array.isArray(list) ? list.find(x => norm(x.slug) === slugNorm) : undefined;
        }
        return entry || null;
      }),
      switchMap(entry => {
        if (!entry) return throwError(() => new Error('SEO_PAGE_NOT_FOUND'));

        // ✅ useLoc sécurisé (évite seo.undefined.json)
        const useLoc = this.safeLocale(entry.locale || loc);
        const coreUrl = `/assets/i18n/${useLoc}.json`;
        const seoUrl = `/assets/i18n/seo.${useLoc}.json`;

        return this.http.get<Record<string, any>>(coreUrl).pipe(
          map(core => { this.i18n.setTranslation(useLoc, core, true); return core; }),
          switchMap(() => this.http.get<Record<string, any>>(seoUrl)),
          map(bundle => {
            this.i18n.setTranslation(useLoc, bundle, true);
            this.i18n.use(useLoc);

            const tBase = `SEO_PAGES.${entry!.tKey}`;
            const title = this.i18n.instant(`${tBase}.TITLE`);
            const meta = this.i18n.instant(`${tBase}.META`);
            const h1 = this.i18n.instant(`${tBase}.H1`);
            const editorial = this.i18n.instant(`${tBase}.EDITORIAL`);

            const q1 = this.i18n.instant(`${tBase}.FAQ_Q1`);
            const a1 = this.i18n.instant(`${tBase}.FAQ_A1`);
            const q2 = this.i18n.instant(`${tBase}.FAQ_Q2`);
            const a2 = this.i18n.instant(`${tBase}.FAQ_A2`);
            const faq = (q1 && a1 ? [{ q: q1, a: a1 }] : [])
              .concat(q2 && a2 ? [{ q: q2, a: a2 }] : []);

            const resolved: ResolvedBridgeSeo = {
              locale: useLoc,
              slug: entry!.slug,
              tKey: entry!.tKey,
              ctaParams: entry!.ctaParams,
              canonicalPath: `/${useLoc}/${entry!.slug}`,
              page: { tKey: entry!.tKey, ctaParams: entry!.ctaParams, title, metaDescription: meta, h1, editorial, faq }
            };
            return resolved;
          })
        );
      })
    );
  }

  // ———————————————————————————————————
  // SWAP resolver (corrigé: safeLocale sur entry.locale || loc)
  // ———————————————————————————————————
  getSwapSeoBySlug(locale: string, slug: string): Observable<ResolvedSeo> {
    const loc = this.safeLocale(locale);
    const norm = (s: string) => (s || '').toLowerCase();

    return this.loadRoutes().pipe(
      map(list => {
        const slugNorm = norm(slug);

        // 1) match strict locale+slug
        let entry = Array.isArray(list)
          ? list.find(x => norm(x.locale) === loc && norm(x.slug) === slugNorm)
          : undefined;

        // 2) sinon: match par slug (toutes locales)
        if (!entry) {
          entry = Array.isArray(list) ? list.find(x => norm(x.slug) === slugNorm) : undefined;
        }

        return entry || null;
      }),
      switchMap(entry => {
        if (!entry) {
          return throwError(() => new Error('SEO_PAGE_NOT_FOUND'));
        }
        
        // ✅ useLoc sécurisé (évite /assets/i18n/undefined.json et /assets/i18n/seo.undefined.json)
        const useLoc = this.safeLocale(entry.locale || loc);
        const coreUrl = `/assets/i18n/${useLoc}.json`;
        const seoUrl = `/assets/i18n/seo.${useLoc}.json`;
        console.log("getSwapSeoBySlug");
        console.log(useLoc);
        console.log(seoUrl);
        return this.http.get<Record<string, any>>(coreUrl).pipe(
          map(core => { this.i18n.setTranslation(useLoc, core, true); return core; }),
          switchMap(() => this.http.get<Record<string, any>>(seoUrl)),
          map(bundle => {
            this.i18n.setTranslation(useLoc, bundle, true);
            this.i18n.use(useLoc);

            const tBase = `SEO_PAGES.${entry!.tKey}`;
            const title = this.i18n.instant(`${tBase}.TITLE`);
            const meta = this.i18n.instant(`${tBase}.META`);
            const h1 = this.i18n.instant(`${tBase}.H1`);
            const editorial = this.i18n.instant(`${tBase}.EDITORIAL`);

            const q1 = this.i18n.instant(`${tBase}.FAQ_Q1`);
            const a1 = this.i18n.instant(`${tBase}.FAQ_A1`);
            const q2 = this.i18n.instant(`${tBase}.FAQ_Q2`);
            const a2 = this.i18n.instant(`${tBase}.FAQ_A2`);
            const faq = (q1 && a1 ? [{ q: q1, a: a1 }] : [])
              .concat(q2 && a2 ? [{ q: q2, a: a2 }] : []);

            const resolved: ResolvedSeo = {
              locale: useLoc,
              slug: entry!.slug,
              tKey: entry!.tKey,
              ctaParams: entry!.ctaParams,
              canonicalPath: `/${useLoc}/${entry!.slug}`,
              page: { tKey: entry!.tKey, ctaParams: entry!.ctaParams, title, metaDescription: meta, h1, editorial, faq }
            };
            return resolved;
          })
        );
      })
    );
  }

  // ———————————————————————————————————
  // Query overrides (inchangé)
  // ———————————————————————————————————
  applyQueryOverrides(
    base: ResolvedSeo,
    q?: Partial<CtaParams & { locale?: string }>
  ): ResolvedSeo {
    if (!q) return base;

    const cta = { ...base.ctaParams };
    if (q.chainId !== undefined) cta.chainId = Number(q.chainId);
    if (q.tokenIn) cta.tokenIn = q.tokenIn.toUpperCase();
    if (q.tokenOut) cta.tokenOut = q.tokenOut.toUpperCase();

    const loc = q.locale ? this.safeLocale(q.locale) : base.locale;

    return {
      ...base,
      locale: loc,
      ctaParams: cta,
      page: { ...base.page, ctaParams: cta },
      canonicalPath: `/${loc}/${base.slug}`
    };
  }

  // ———————————————————————————————————
  // Defaults (inchangé)
  // ———————————————————————————————————
  getDefaults() {
    return {
      brand: 'SwapTokens',
      robots: 'index,follow',
      image: '/assets/og/default.png',
      faqNotes: [
        'Les informations fournies ne constituent pas un conseil financier.',
        'Vérifiez les adresses des tokens avant de swapper.'
      ],
      siteName: 'SwapTokens',
      titleSuffix: ' · SwapTokens',
      defaultDescription: 'Swap instantané sur EVM (Polygon, Arbitrum, BSC, Ethereum).',
      twitterCard: 'summary_large_image'
    };
  }

  // ———————————————————————————————————
  // JSON-LD FAQ (inchangé)
  // ———————————————————————————————————
  buildFaqJsonLd(resolved: ResolvedSeo, url?: string | null): string | null {
    try {
      const tBase = `SEO_PAGES.${resolved.tKey}`;
      const q1 = this.i18n.instant(`${tBase}.FAQ_Q1`);
      const a1 = this.i18n.instant(`${tBase}.FAQ_A1`);
      const q2 = this.i18n.instant(`${tBase}.FAQ_Q2`);
      const a2 = this.i18n.instant(`${tBase}.FAQ_A2`);

      const mainEntity: any[] = [];
      if (q1 && a1) mainEntity.push({ '@type': 'Question', name: q1, acceptedAnswer: { '@type': 'Answer', text: a1 } });
      if (q2 && a2) mainEntity.push({ '@type': 'Question', name: q2, acceptedAnswer: { '@type': 'Answer', text: a2 } });
      if (!mainEntity.length) return null;

      const data: any = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity
      };
      if (url) data['@id'] = url;

      return JSON.stringify(data);
    } catch {
      return null;
    }
  }
}
